<template>
  <a-layout-sider
    v-model="sidebarCollapsed"
    :trigger="null"
    collapsible
    breakpoint="lg"
    :style="{
      overflow: 'auto',
      height: '100vh',
      position: 'fixed',
      left: 0,
      zIndex: 1000,
    }"
  >
    <a href="/">
      <div class="logo">
        <BrandLogo :type="sidebarCollapsed ? 'logo' : 'full'" color="white" />
      </div>
    </a>

    <div v-if="showTaskButton" class="sidebar-task-button">
      <TaskDrawerButton
        :collapsed="sidebarCollapsed"
        :taskCount="taskCount"
        @onToggleTaskDrawer="$emit('onToggleTaskDrawer')"
      />
    </div>

    <a-menu
      id="app-menu"
      theme="dark"
      mode="vertical"
      :selectedKeys="activeMenus"
    >
      <template v-for="item in menuItems">
        <a-menu-item v-if="!item.submenus" :key="item.key">
          <MenuItemLink :item="item" :collapsed="sidebarCollapsed" />
        </a-menu-item>
        <a-sub-menu
          v-else
          :key="item.key"
          :class="subMenuClass(item)"
          popupClassName="subMenu"
        >
          <span slot="title" class="submenu-title">
            <MenuItemLink
              :item="item"
              :collapsed="sidebarCollapsed"
              style="color: rgba(255, 255, 255, 0.65)"
            />
          </span>
          <template v-for="submenu in item.submenus">
            <a-menu-item v-if="!submenu.submenus" :key="submenu.key">
              <MenuItemLink
                :item="submenu"
                :collapsed="sidebarCollapsed"
                style="color: rgba(255, 255, 255, 0.65)"
              />
            </a-menu-item>
            <a-sub-menu
              v-else
              :key="submenu.key"
              :class="subMenuClass(submenu)"
              popupClassName="subMenu"
            >
              <span slot="title">
                <MenuItemLink
                  :item="submenu"
                  :collapsed="sidebarCollapsed"
                  style="color: rgba(255, 255, 255, 0.65)"
                />
              </span>
              <a-menu-item
                v-for="subSubmenu in submenu.submenus"
                :key="subSubmenu.key"
              >
                <MenuItemLink
                  :item="subSubmenu"
                  :collapsed="sidebarCollapsed"
                />
              </a-menu-item>
            </a-sub-menu>
          </template>
        </a-sub-menu>
      </template>
    </a-menu>

    <div v-if="showSideBarCta" class="free-trial-sidebar-cta">
      <a-button
        class="sidebar-cta-button"
        type="primary"
        @click="$emit('sideBarCtaClicked')"
      >
        <a
          :href="demoLink"
          target="_blank"
          :class="{
            'sidebar-cta-button-content': true,
            'sider-collapsed': sidebarCollapsed,
          }"
        >
          <img src="/free-trial-graeham-icon.png" alt="Schedule Demo" />
          <span v-if="!sidebarCollapsed" style="padding-left: 5px"
            >Schedule Demo</span
          >
        </a>
      </a-button>
    </div>
  </a-layout-sider>
</template>

<script>
import MenuItemLink from "@/components/MenuItemLink.vue";
import TaskDrawerButton from "@/tasks/TaskDrawerButton.vue";
import BrandLogo from "@/components/BrandLogo.vue";
import { getSidebarBackgroundColor } from "@/shared/helpers.js";

export default {
  components: {
    MenuItemLink,
    TaskDrawerButton,
    BrandLogo,
  },

  props: {
    menuItems: {
      type: Array,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: true,
    },
    taskCount: {
      type: Number,
      required: false,
      default: 0,
    },
    showTaskButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSideBarCta: {
      type: Boolean,
      required: false,
      default: false,
    },
    demoLink: {
      type: String,
      required: false,
      default: "",
    },
    environment: {
      type: String,
      required: false,
      default: "production",
    },
  },

  data() {
    return {
      sidebarCollapsed: this.collapsed,
    };
  },

  computed: {
    sidebarBackgroundColor() {
      return getSidebarBackgroundColor(this.environment);
    },
    activeMenus() {
      const currentPath = window.location.pathname;
      const currentHash = window.location.hash;
      const currentUrl = currentPath + currentHash;
      const flattenedItems = this.flattenMenuItems(this.menuItems || []);
      let bestMatchKeys = [];

      for (const item of flattenedItems) {
        // return early if exact match is found
        if (item.url && item.url === currentUrl) {
          bestMatchKeys = item.keyPath;
          break;
        }

        if (item.url && currentPath.startsWith(item.url)) {
          bestMatchKeys = item.keyPath;
        }
      }

      return bestMatchKeys;
    },
  },

  watch: {
    collapsed(newValue) {
      this.sidebarCollapsed = newValue;
    },
    sidebarCollapsed(newValue) {
      this.$emit("update:collapsed", newValue);
    },
  },

  methods: {
    // Recursively flatten menu items into a single array.
    // This is used to find the active menu item based on the current URL.
    flattenMenuItems(items, parentKeys = [], isTopLevel = true) {
      const flattened = [];

      // Take each menu item:
      // If it has submenus, recursively flatten them along with the path to the nested menu item.
      // Include all permutations except top-level menu items with submenus, since we only ever want to match on their children.
      items.forEach((item) => {
        // some menu items should never be highlighted
        if (item.highlight === false) {
          return;
        }

        if (item.submenus && item.submenus.length > 0) {
          if (!isTopLevel) {
            flattened.push({
              ...item,
              keyPath: [...parentKeys, item.key],
            });
          }

          flattened.push(
            ...this.flattenMenuItems(
              item.submenus,
              [...parentKeys, item.key],
              false,
            ),
          );
        } else {
          flattened.push({
            ...item,
            keyPath: [...parentKeys, item.key],
          });
        }
      });

      return flattened;
    },
    subMenuClass(item) {
      if (this.activeMenus.includes(item.key)) {
        return "ant-menu-item-selected";
      }
    },
  },
};
</script>

<style scoped lang="scss">
#app-menu,
.ant-layout-sider {
  background: v-bind(sidebarBackgroundColor);
  transition: background-color 0.5s ease;
}

.logo {
  margin: 16px 16px;
  text-align: center;
}

.logo :deep(img) {
  height: 28px;
}

.ant-menu-item :deep(.ant-badge-status-error),
.ant-menu-submenu :deep(.ant-badge-status-error),
.ant-menu-submenu .ant-menu-item :deep(.ant-badge-status-error) {
  margin-left: 10px;
}

.submenu-title,
.submenu-title > :deep(a) {
  display: block;
}

.divider-top-border {
  box-shadow:
    0 -4px 0 #001529,
    0 -5px #595959;
}

.free-trial-sidebar-cta,
.sidebar-task-button {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.sidebar-cta-button {
  width: 92%;
  height: 100%;
  padding-top: 5px;
  padding-bottom: 5px;
  border: 1px solid #e6f7ff;
}

.sidebar-cta-button-content {
  display: flex;
  justify-content: space-evenly;
  align-items: center;

  &.sider-collapsed {
    justify-content: center;
  }
}

.sidebar-cta-button img {
  max-height: 24px;
  border-radius: 50%;
  border: 1px solid #e6f7ff;
}

.task-button {
  height: 100%;
  width: 94%;
  padding: 5px;
  color: #ffffff;
  background: #ffffff26;
  border: 1px solid #d9d9d926;

  &:hover {
    background: #1890ff;
  }
}

.task-button-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-shadow: none;

  &.sider-collapsed {
    justify-content: center;
  }
}
</style>
